/*
 * Recurring Trip Templates — admin panel
 * Creates/edits templates that spawn trip instances daily.
 */
import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { apiClient } from "../../lib/api";
import AddressAutocomplete from "../../components/AddressAutocomplete";
import PhoneInput, { isValidE164 } from "../../components/PhoneInput";
import { confirm } from "../../components/ConfirmModal";

const TP = "/tpt";

const DAYS = [
  ["M", 0], ["T", 1], ["W", 2], ["Th", 3], ["F", 4], ["Sa", 5], ["Su", 6],
];

function TemplateEditor({ template, providers, onClose, onSaved }) {
  const initial = template || {
    tp_id: providers[0]?.tp_id || "",
    rider: { name: "", phone: "", mobility_type: "ambulatory", medical_notes: "" },
    pickup: { address: "", lat: null, lng: null, notes: "" },
    dropoff: { address: "", lat: null, lng: null, facility_name: "" },
    days_of_week: [0, 2, 4],
    pickup_time_local: "08:00",
    timezone_offset_minutes: -300,
    estimated_duration_min: 30, estimated_distance_mi: 8,
    vehicle_requirements: {},
    start_date: new Date().toISOString().slice(0, 10),
    end_date: "",
    active: true, notes: "",
  };
  const [form, setForm] = useState(initial);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const setN = (path, v) => {
    const [a, b] = path.split(".");
    setForm(f => ({ ...f, [a]: { ...(f[a] || {}), [b]: v } }));
  };
  const toggleDay = (d) => {
    setForm(f => {
      const next = new Set(f.days_of_week);
      if (next.has(d)) next.delete(d); else next.add(d);
      return { ...f, days_of_week: [...next].sort((a,b)=>a-b) };
    });
  };
  const save = async () => {
    if (!form.rider.name) { toast.error("Rider name required"); return; }
    if (form.rider.phone && !isValidE164(form.rider.phone)) { toast.error("Rider phone must be E.164 (+1XXXXXXXXXX)"); return; }
    if (!form.pickup.address || form.pickup.lat == null || form.pickup.lng == null) { toast.error("Pick pickup address from suggestions"); return; }
    if (!form.dropoff.address || form.dropoff.lat == null || form.dropoff.lng == null) { toast.error("Pick drop-off address from suggestions"); return; }
    if (form.days_of_week.length === 0) { toast.error("Pick at least one day"); return; }
    setBusy(true);
    try {
      const payload = { ...form };
      if (!payload.end_date) delete payload.end_date;
      if (template?.template_id) {
        await apiClient.patch(`${TP}/templates/${template.template_id}`, payload);
      } else {
        await apiClient.post(`${TP}/templates`, payload);
      }
      toast.success("Template saved");
      onSaved(); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };
  const del = async () => {
    if (!template?.template_id) return;
    const ok = await confirm({
      title: "Delete this recurring template?",
      body: `Existing spawned trips will remain, but no new instances will spawn for "${template.rider?.name || "this template"}".`,
      confirmLabel: "Delete template",
      tone: "danger",
    });
    if (!ok) return;
    await apiClient.delete(`${TP}/templates/${template.template_id}`);
    toast.info("Template deleted");
    onSaved(); onClose();
  };
  return (
    <div className="fixed inset-0 bg-black/50 z-[9999] grid place-items-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl max-w-2xl w-full p-6 max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="tpl-editor">
        <div className="text-lg font-bold mb-1">{template?.template_id ? "Edit recurring trip" : "New recurring trip"}</div>
        <div className="text-xs text-slate-500 mb-4">Auto-spawns a trip on each selected day. Assign a driver from the TP portal or use auto-assign.</div>

        <div className="text-xs uppercase tracking-wider text-slate-500 mb-2">Days & schedule</div>
        <div className="flex gap-2 mb-3">
          {DAYS.map(([label, val]) => (
            <button key={val} onClick={() => toggleDay(val)} type="button" data-testid={`tpl-day-${val}`}
              className={`w-9 h-9 rounded-full text-xs font-semibold ${form.days_of_week.includes(val) ? "bg-forest text-white" : "bg-slate-100 text-slate-500 border border-[#E2E0D8]"}`}>
              {label}
            </button>
          ))}
        </div>
        <div className="grid grid-cols-3 gap-3 mb-4">
          <label>
            <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">Pickup time (local)</div>
            <input type="time" value={form.pickup_time_local} onChange={(e) => set("pickup_time_local", e.target.value)} className="w-full border border-[#E2E0D8] rounded-md px-3 py-2 text-sm" data-testid="tpl-time"/>
          </label>
          <label>
            <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">TZ offset (min)</div>
            <input type="number" value={form.timezone_offset_minutes} onChange={(e) => set("timezone_offset_minutes", Number(e.target.value))} className="w-full border border-[#E2E0D8] rounded-md px-3 py-2 text-sm"/>
          </label>
          <label>
            <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">Provider</div>
            <select value={form.tp_id} onChange={(e) => set("tp_id", e.target.value)} className="w-full border border-[#E2E0D8] rounded-md px-3 py-2 text-sm">
              {providers.map(tp => <option key={tp.tp_id} value={tp.tp_id}>{tp.name}</option>)}
            </select>
          </label>
          <label>
            <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">Start date</div>
            <input type="date" value={form.start_date} onChange={(e) => set("start_date", e.target.value)} className="w-full border border-[#E2E0D8] rounded-md px-3 py-2 text-sm"/>
          </label>
          <label>
            <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">End date (optional)</div>
            <input type="date" value={form.end_date} onChange={(e) => set("end_date", e.target.value)} className="w-full border border-[#E2E0D8] rounded-md px-3 py-2 text-sm"/>
          </label>
        </div>

        <div className="text-xs uppercase tracking-wider text-slate-500 mb-2">Rider</div>
        <div className="grid grid-cols-2 gap-3 mb-4">
          <input placeholder="Name" value={form.rider.name} onChange={(e) => setN("rider.name", e.target.value)} className="border border-[#E2E0D8] rounded-md px-3 py-2 text-sm" data-testid="tpl-rider-name"/>
          <PhoneInput value={form.rider.phone} onChange={(v) => setN("rider.phone", v)} testid="tpl-rider-phone" placeholder="Phone (+1…)" />
          <select value={form.rider.mobility_type} onChange={(e) => setN("rider.mobility_type", e.target.value)} className="border border-[#E2E0D8] rounded-md px-3 py-2 text-sm">
            <option value="ambulatory">Ambulatory</option>
            <option value="wheelchair">Wheelchair</option>
            <option value="stretcher">Stretcher</option>
          </select>
          <input placeholder="Medical notes" value={form.rider.medical_notes} onChange={(e) => setN("rider.medical_notes", e.target.value)} className="border border-[#E2E0D8] rounded-md px-3 py-2 text-sm"/>
        </div>

        <div className="text-xs uppercase tracking-wider text-slate-500 mb-2">Pickup & drop-off</div>
        <div className="grid grid-cols-1 gap-3 mb-4">
          <div>
            <AddressAutocomplete
              value={form.pickup.address}
              onChange={(v) => setN("pickup.address", v)}
              onPlace={(p) => setForm((f) => ({ ...f, pickup: { ...f.pickup, address: p.formatted_address, lat: p.lat, lng: p.lng } }))}
              testid="tpl-pickup-addr"
              placeholder="Pickup address"
            />
            {form.pickup.lat != null && <div className="text-[11px] text-slate-500 mt-1">Verified · {Number(form.pickup.lat).toFixed(5)}, {Number(form.pickup.lng).toFixed(5)}</div>}
          </div>
          <div>
            <AddressAutocomplete
              value={form.dropoff.address}
              onChange={(v) => setN("dropoff.address", v)}
              onPlace={(p) => setForm((f) => ({ ...f, dropoff: { ...f.dropoff, address: p.formatted_address, lat: p.lat, lng: p.lng } }))}
              testid="tpl-dropoff-addr"
              placeholder="Drop-off address"
            />
            {form.dropoff.lat != null && <div className="text-[11px] text-slate-500 mt-1">Verified · {Number(form.dropoff.lat).toFixed(5)}, {Number(form.dropoff.lng).toFixed(5)}</div>}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <input placeholder="Est duration (min)" type="number" value={form.estimated_duration_min || ""} onChange={(e) => set("estimated_duration_min", Number(e.target.value))} className="border border-[#E2E0D8] rounded-md px-3 py-2 text-sm"/>
            <input placeholder="Est distance (mi)" type="number" value={form.estimated_distance_mi || ""} onChange={(e) => set("estimated_distance_mi", Number(e.target.value))} className="border border-[#E2E0D8] rounded-md px-3 py-2 text-sm"/>
          </div>
        </div>

        <label className="flex items-center gap-2 mb-4">
          <input type="checkbox" checked={!!form.active} onChange={(e) => set("active", e.target.checked)}/>
          <span className="text-sm">Active — spawner will create trips for this template</span>
        </label>

        <div className="flex gap-2 justify-between">
          {template?.template_id ? <button onClick={del} className="text-sm text-red-600">Delete</button> : <div/>}
          <div className="flex gap-2">
            <button onClick={onClose} className="px-4 py-2 rounded-md border border-[#E2E0D8] text-sm">Cancel</button>
            <button onClick={save} disabled={busy} className="px-4 py-2 rounded-md bg-forest text-white text-sm font-semibold" data-testid="tpl-save">
              {busy ? "Saving…" : "Save template"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function TemplatesPanel() {
  const [templates, setTemplates] = useState([]);
  const [providers, setProviders] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = useCallback(async () => {
    try {
      const [tR, pR] = await Promise.all([
        apiClient.get(`${TP}/templates`),
        apiClient.get(`${TP}/providers`),
      ]);
      setTemplates(tR.data || []);
      setProviders(pR.data || []);
    } catch (e) { toast.error("Load failed"); }
  }, []);
  useEffect(() => { load(); }, [load]);
  const dayLabels = (arr) => (arr || []).map(v => DAYS.find(([, val]) => val === v)?.[0]).filter(Boolean).join(" ");
  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-bold text-slate_navy">Recurring trips</h2>
          <p className="text-sm text-slate-500">Templates auto-spawn a real trip each occurrence, ready for assignment.</p>
        </div>
        <button onClick={() => setEditing("new")} className="bg-forest text-white px-4 py-2 rounded-md text-sm font-semibold" data-testid="new-tpl-btn">+ New recurring trip</button>
      </div>
      {templates.length === 0 ? (
        <div className="border border-dashed border-[#E2E0D8] rounded-xl p-10 text-center text-slate-500">No recurring trips yet.</div>
      ) : (
        <div className="grid md:grid-cols-2 gap-3">
          {templates.map(t => (
            <div key={t.template_id} className="bg-white border border-[#E9EAE4] rounded-xl p-4" data-testid={`tpl-${t.template_id}`}>
              <div className="flex justify-between items-start">
                <div>
                  <div className="font-semibold text-slate_navy">{t.rider?.name || "—"}</div>
                  <div className="text-xs text-slate-500">{t.rider?.mobility_type} · {t.pickup_time_local} · {dayLabels(t.days_of_week)}</div>
                </div>
                <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${t.active ? "bg-green-100 text-green-800" : "bg-slate-200 text-slate-700"}`}>{t.active ? "active" : "paused"}</span>
              </div>
              <div className="text-xs text-slate-600 mt-2">{(t.pickup?.address || "").split(",")[0]} → {(t.dropoff?.address || "").split(",")[0]}</div>
              <div className="mt-3">
                <button onClick={() => setEditing(t)} className="text-xs border border-[#E2E0D8] px-3 py-1 rounded-md">Edit</button>
              </div>
            </div>
          ))}
        </div>
      )}
      {editing && <TemplateEditor template={editing === "new" ? null : editing} providers={providers} onClose={() => setEditing(null)} onSaved={load}/>}
    </div>
  );
}
